import React, { FC } from 'react';
import {
  BodyText,
  ButtonAsWrapper,
  DetailTextLarge,
  FlexCol,
  FlexProps,
  FlexRow,
  H2Text,
  H4Text,
} from '@minecraft.atoms';
import { FormattedPriceMonth, TranslateFn, useTranslation } from '@blocs.i18n';
import { DesignSystemTheme } from '@minecraft.themes';
import { DiscountTypeEnum, PlanTermEnum } from '@minecraft.graphql-operations';
import { WindowWidthContext } from '@minecraft.utils';
import { VerticalPlanProps } from './types';
import { PlanCode, TemperatureValues } from '../enums/plans';
import { priceFormatter } from '../helpers/priceFormatter';
import { PlanType } from '../usePlans';
import { FeatureList } from '../PlanSelection/FeatureList';

type PlanFeatureCardProps = {
  plan?: VerticalPlanProps['plan'];
  children?: React.ReactNode;
  title?: string;
  billed?: string;
  price?: number;
  billingCode?: string;
  temperature?: TemperatureValues;
  countryCode?: string;
  disableTrial?: boolean;
  isFreePlan?: boolean;
  setSelectedPlan?: (plan: VerticalPlanProps['plan']) => void;
  isSelectedPlan?: boolean;
};

type BorderPropsParams = {
  isFreePlan: boolean;
  isSelectedPlan: boolean;
  isReppedRegDiscount: boolean;
  temperature: TemperatureValues;
};

const getBorderProps = (borderPropsParams: BorderPropsParams): FlexProps['customBorders']['top'] | undefined => {
  if (borderPropsParams.isReppedRegDiscount) {
    return {
      borderColor: borderPropsParams.isSelectedPlan ? `${borderPropsParams.temperature}Paywall` : 'default',
      borderSize: borderPropsParams.isSelectedPlan ? 0.18 : 0.1,
      borderStyle: 'solid',
    };
  }

  if (!borderPropsParams.isSelectedPlan) {
    return undefined;
  }

  return {
    borderColor: `${borderPropsParams.temperature}Paywall`,
    borderSize: borderPropsParams.isFreePlan ? 0.18 : 0.12,
    borderStyle: 'solid',
  };
};

const getCustomBorderProps = (borderPropsParams: BorderPropsParams): FlexProps['customBorders'] | undefined => {
  const borderProps = getBorderProps(borderPropsParams);

  if (!borderProps) return undefined;
  return {
    top: borderProps,
    left: borderProps,
    right: borderProps,
    bottom: borderProps,
  };
};

const PriceComponent = ({ plan, countryCode, t }: { plan: PlanType; countryCode: string; t: TranslateFn }) => {
  const strikeThroughPrice =
    Number(plan?.strikeThroughPrice) > 0
      ? priceFormatter({
          price: Number(plan?.strikeThroughPrice),
          currencyCode: plan?.currencyCode,
          countryCode,
          roundingFloor: true,
          t,
        })
      : null;

  return (
    <FlexRow alignItems="end" gap="1">
      {strikeThroughPrice && (
        <H2Text fontWeight="medium" className="cn_line-through" color="lowContrastText">
          {strikeThroughPrice}
        </H2Text>
      )}
      <FormattedPriceMonth
        price={Number(plan?.price)}
        planCurrencyCode={plan?.currencyCode}
        countryCode={countryCode}
        planTermCode={plan?.term?.code}
        PriceComponent={<H2Text fontWeight="medium" color="inverted" />}
        LabelComponent={<BodyText color="inverted" />}
        isSingleItemPrice={false}
      />
    </FlexRow>
  );
};

const PlanFeatureCardHeader: FC<Omit<PlanFeatureCardProps, 'children' | 'price'>> = ({
  billingCode,
  title,
  billed,
  plan,
  temperature,
  countryCode,
  disableTrial,
  isFreePlan,
}) => {
  const { t } = useTranslation();
  const Text = isFreePlan ? H2Text : H4Text;
  const isReppedRegDiscount = plan.discountType === DiscountTypeEnum.ReppedRegistrationDiscount;
  const isReppedRegDiscountOnMonthly = isReppedRegDiscount && plan.term.code === PlanTermEnum.Monthly;

  return (
    <FlexRow
      w="full"
      className="cn_atom_px-3 cn_atom_py-5"
      borderRadius="sm"
      role="list"
      bgColor={`${temperature}Paywall` as keyof DesignSystemTheme['bgColors']}
      aria-label={`${billingCode} plan options`}
      alignItems="center"
    >
      <FlexCol ml="4" gap="3" w="full">
        <FlexRow justifyContent="space-between" gap="3" alignItems="center">
          <Text fontWeight="semibold" color={`${temperature}Paywall` as keyof DesignSystemTheme['textColors']}>
            {title}
          </Text>
        </FlexRow>
        {!isFreePlan && (
          <>
            {disableTrial || isReppedRegDiscount ? (
              <FlexCol gap="1">
                <FlexRow
                  justifyContent="space-between"
                  gap="1"
                  alignItems="flex-end"
                  data-testid={`${plan?.billingCode}-plan-price-container`}
                >
                  <PriceComponent plan={plan} countryCode={countryCode} t={t} />
                  {isReppedRegDiscountOnMonthly && (
                    <BodyText color="inverted"> {t('common:paywall.plan.billed_for_3_months')}</BodyText>
                  )}
                </FlexRow>
                {isReppedRegDiscount &&
                  (isReppedRegDiscountOnMonthly ? (
                    <DetailTextLarge color="inverted">
                      {t('common:paywall.plan.trial.m2m.after_trial_price', {
                        price: priceFormatter({
                          price: Number(plan.strikeThroughPrice),
                          currencyCode: plan.currencyCode,
                          t,
                        }),
                      })}
                    </DetailTextLarge>
                  ) : (
                    <DetailTextLarge color="inverted">{t('common:paywall.plan.billed_yearly')}</DetailTextLarge>
                  ))}
              </FlexCol>
            ) : (
              <FlexCol gap="1" w="full">
                <H2Text as="h2" fontWeight="bold" color="inverted">
                  {t('common:paywall.checkout.upsellFree', { days: plan?.trialLengthDays ?? 14 })}
                </H2Text>
                <DetailTextLarge align="center" color="inverted">
                  {billed}
                </DetailTextLarge>
              </FlexCol>
            )}
          </>
        )}
      </FlexCol>
    </FlexRow>
  );
};

export const PlanFeatureCard: FC<PlanFeatureCardProps> = React.memo(
  ({
    children,
    plan,
    billed,
    title,
    billingCode,
    temperature,
    countryCode,
    disableTrial,
    isFreePlan,
    setSelectedPlan,
    isSelectedPlan,
  }) => {
    const isMobile = WindowWidthContext.useIsMobile();
    const isReppedRegDiscount = plan.discountType === DiscountTypeEnum.ReppedRegistrationDiscount;
    const showBorderAndChildren = isReppedRegDiscount ? true : isSelectedPlan;

    return (
      <ButtonAsWrapper
        onClick={() => {
          setSelectedPlan?.(plan);
        }}
        aria-label={`${billingCode} plan wrapper`}
        data-testid={`plan-option-container-${billingCode}`}
        style={{ width: isMobile ? '80%' : '100%' }}
      >
        <FlexCol className="cn_overflow-hidden" borderRadius="sm" w="full">
          <FlexCol
            w="full"
            className={showBorderAndChildren ? 'cn_atom_p-2' : 'cn_atom_p-0'}
            borderRadius="sm"
            grow={0}
            customBorders={getCustomBorderProps({ isFreePlan, isReppedRegDiscount, isSelectedPlan, temperature })}
          >
            <PlanFeatureCardHeader
              plan={plan}
              billingCode={billingCode}
              title={title}
              billed={billed}
              temperature={temperature}
              countryCode={countryCode}
              disableTrial={disableTrial}
              isFreePlan={isFreePlan}
            />
            {showBorderAndChildren && <>{children}</>}
          </FlexCol>
        </FlexCol>
      </ButtonAsWrapper>
    );
  }
);

export const PlanFeatureCardList: FC<{
  plans: PlanType[];
  selectedPlan?: PlanType;
  isReppedRegDiscount?: boolean;
  countryCode?: string;
  setSelectedPlan?: (plan: PlanType) => void;
  disableTrial?: boolean;
}> = ({ plans, countryCode, setSelectedPlan, isReppedRegDiscount, selectedPlan, disableTrial }) => {
  const { t } = useTranslation();
  return (
    <FlexCol gap="6" w="full">
      {plans.map((plan) => {
        const isFreePlan = plan?.code === PlanCode.Free;
        const isPremiumPlan = plan?.code === PlanCode.Premium;
        const isSelectedPlan = selectedPlan?.billingCode === plan?.billingCode;
        if (!isPremiumPlan && !isFreePlan) return null;
        return (
          <PlanFeatureCard
            key={plan?.billingCode}
            plan={plan}
            billed={plan?.billed}
            price={Number(plan?.price)}
            title={isFreePlan || isReppedRegDiscount ? plan.title : t('common:membership')}
            billingCode={plan?.billingCode}
            temperature={plan?.temperature}
            countryCode={countryCode}
            isFreePlan={isFreePlan}
            setSelectedPlan={setSelectedPlan}
            isSelectedPlan={isSelectedPlan}
            disableTrial={disableTrial}
          >
            <FlexCol w="full" h="full" className="cn_atom_px-2" justifyContent="flex-start" gap="2" mt="4">
              <FeatureList
                iconName={plan.iconName || 'check-circle'}
                features={plan.keyFeatures}
                temperature={plan?.temperature}
              />
            </FlexCol>
          </PlanFeatureCard>
        );
      })}
    </FlexCol>
  );
};
